import React from 'react';
import { Navigate, useLocation, useRoutes } from 'react-router-dom';
import AuthLayout from '../layout/auth';
import MainLayout from '../layout/main';
import PublicLayout from '../layout/public';
import BlankLayout from '../layout/blank';
import {
  authRoutes,
  mainRoutes,
  profileRoute,
  deleteAccountRoute,
  pharmacyRoutes,
  subscriptionRoutes,
  signupRoutes,
  confirmationRoutes,
  scanCheckoutRoutes,
  planRoutes,
  planCoreRoutes,
  preScanQuestions,
  preScanQuestionnairePdfRoutes,
  planPlusRoutes,
  scanLandingRoutes,
  completeOnboardingRoutes,
  bloodOnlyCheckoutRoutes,
  lp1Routes,
  annualScanRoutes,
} from './routes';
import PlansCore from '../pages/plans-core';
import PageListener from '../components/PageListener';
import AppUpsellBloods from '../pages/upgrade-plan';


const LegacyPersonalisedPlanRedirect: React.FC<{ to: string }> = ({ to }) => {
  const location = useLocation();
  return <Navigate to={`${to}${location.search}${location.hash}`} replace />;
};

export const AppRouter: React.FC = () => {
  const routes = useRoutes([
    {
      path: '/signup',
      element: <PublicLayout />,
      children: [
        ...signupRoutes.map((route) => ({
          path: route.path,
          element: route.element,
        })),
      ],
    },
    {
      path: '/',
      element: <MainLayout />,
      children: [
        ...mainRoutes.map((route) => ({
          path: route.path,
          element: route.element,
        })),
      ],
    },
    {
      path: '/admin/87431',
      element: <AuthLayout />,
      children: [
        ...authRoutes.map((route) => ({
          path: route.path,
          element: route.element,
        })),
      ],
    },
    {
      path: '/complete-profile',
      element: <AuthLayout />,
      children: [
        ...profileRoute.map((route) => ({
          path: route.path,
          element: route.element,
        })),
      ],
    },
    {
      path: '/delete-account',
      element: <AuthLayout />,
      children: [
        ...deleteAccountRoute.map((route) => ({
          path: route.path,
          element: route.element,
        })),
      ],
    },
    {
      path: '/book-test',
      element: <AuthLayout />,
      children: [
        ...pharmacyRoutes.map((route) => ({
          path: route.path,
          element: route.element,
        })),
      ],
    },
    {
      path: '/subscription',
      element: <AuthLayout />,
      children: [
        {
          index: true,
          element: subscriptionRoutes.find(r => r.path === '/subscription')?.element,
        },
      ],
    },
    {
      path: '/complete-subscription',
      element: <AuthLayout />,
      children: [
        {
          index: true,
          element: subscriptionRoutes.find(r => r.path === '/complete-subscription')?.element,
        },
      ],
    },
    {
      path: '/plans/core',
      element: <PublicLayout />,
      children: [
        {
          index: true,
          element: <PlansCore />,
        },
      ],
    },
    {
      path: '/plans-plus',
      element: <PublicLayout />,
      children: [
        ...planPlusRoutes.map((route) => ({
          path: route.path,
          element: route.element,
        })),
      ],
    },
    {
      path: '/plans',
      element: <PublicLayout />,
      children: [
        ...planRoutes.map((route) => ({
          path: route.path,
          element: route.element,
        })),
      ],
    },
    {
      path: '/add-ons-plan',
      element: <PublicLayout />,
      children: [
        ...bloodOnlyCheckoutRoutes.map((route) => ({
          path: route.path,
          element: route.element,
        })),
      ],
    },
    {
      path: '/blood-only-checkout',
      element: <PublicLayout />,
      children: [
        ...bloodOnlyCheckoutRoutes.map((route) => ({
          path: route.path,
          element: route.element,
        })),
      ],
    },
    {
      path: '/pre-scan-questions',
      element: <BlankLayout />,
      children: [
        ...preScanQuestions.map((route) => ({
          path: route.path,
          element: route.element,
        })),
      ],
    },
    {
      path: '/pre-scan-questionnaire-pdf',
      element: <BlankLayout />,
      children: [
        ...preScanQuestionnairePdfRoutes.map((route) => ({
          path: route.path,
          element: route.element,
        })),
      ],
    },
    {
      path: '/plans-core',
      element: <PublicLayout />,
      children: [
        ...planCoreRoutes.map((route) => ({
          path: route.path,
          element: route.element,
        })),
      ],
    },
    {
      path: '/full-body-scan',
      element: <PublicLayout logo={false} />,
      children: [
        ...scanCheckoutRoutes.map((route) => ({
          path: route.path,
          element: route.element,
        })),
      ],
    },
    {
      path: '/plans-scan',
      element: <PublicLayout />,
      children: [
        ...scanLandingRoutes.map((route) => ({
          path: route.path,
          element: route.element,
        })),
      ],
    },
    {
      path: '/confirmation',
      element: <PublicLayout />,
      children: [
        ...confirmationRoutes.map((route) => ({
          path: route.path,
          element: route.element,
        })),
      ],
    },
    {
      path: '/complete-onboarding',
      element: <AuthLayout />,
      children: [
        {
          index: true,
          element: completeOnboardingRoutes.find(r => r.path === '/complete-onboarding')?.element,
        },
      ],
    },
    {
      path: '/upgrade-plan',
      element: <PublicLayout logo={false} />,
      children: [
        {
          index: true,
          element: <AppUpsellBloods />,
        },
      ],
    },
    {
      path: '/upsell-bloods-preview',
      element: <PublicLayout logo={false} />,
      children: [
        {
          index: true,
          element: <AppUpsellBloods />,
        },
      ],
    },
    {
      path: '/personalised-plan',
      element: <PublicLayout logo={false} />,
      children: [
        ...lp1Routes.map((route) => ({
          path: route.path,
          element: route.element,
        })),
      ],
    },
    {
      path: '/annual-scan',
      element: <PublicLayout logo={false} />,
      children: [
        ...annualScanRoutes.map((route) => ({
          path: route.path,
          element: route.element,
        })),
      ],
    },
    {
      path: '/lp-1',
      element: <LegacyPersonalisedPlanRedirect to="/personalised-plan" />,
    },
    {
      path: '/lp-1/payment-successful',
      element: <LegacyPersonalisedPlanRedirect to="/personalised-plan/payment-successful" />,
    },
  ]);

  return (
    <>
      <PageListener />
      {routes}
    </>
  );
};
